/** Settings → Blocked users: everyone you blocked, with Unblock (docs/release/CONTENT_SAFETY.md). */
import './moderation.css';
import { useEffect, useState } from 'react';
import type { BlockedUser } from '@apos/shared';
import { Button, Panel, toast } from '../../components/ui';
import { api, errorMessage } from '../../lib/api';
import { useMission } from '../../state/mission';

export function BlockedUsersPanel() {
  const [list, setList] = useState<BlockedUser[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api.moderation
      .blocks()
      .then((l) => live && setList(l))
      .catch((e) => live && setError(errorMessage(e)));
    return () => {
      live = false;
    };
  }, []);

  const unblock = async (u: BlockedUser) => {
    setBusy(u.userId);
    try {
      await api.moderation.unblock(u.userId);
      setList((l) => (l ?? []).filter((x) => x.userId !== u.userId));
      await useMission.getState().reloadRecord();
      toast({ title: `${u.displayName} unblocked`, body: 'Their messages, marks and crew radio show again.', tone: 'success' });
    } catch (e) {
      toast({ title: 'Could not unblock', body: errorMessage(e), tone: 'alert' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <Panel title="Blocked users" eyebrow="Safety" data-testid="blocked-users">
      <p className="muted small">
        You do not see team-chat messages, mark text, voice notes or crew radio from people you block. They are not told. Block someone from the flag on their message, mark or crew row.
      </p>
      {error ? (
        <p role="alert" className="small">
          Could not load blocked users: {error}
        </p>
      ) : list === null ? (
        <p className="muted small">Loading…</p>
      ) : list.length === 0 ? (
        <p className="muted small" data-testid="blocked-empty">
          You have not blocked anyone.
        </p>
      ) : (
        <ul className="blocked-list">
          {list.map((u) => (
            <li key={u.userId} data-testid="blocked-user">
              <span>{u.displayName}</span>
              <Button size="sm" variant="secondary" loading={busy === u.userId} disabled={busy !== null} onClick={() => void unblock(u)}>
                Unblock
              </Button>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
